import { DOCUMENT, Service, inject, signal } from '@angular/core';
import { SaphirColorScheme, SaphirThemeContext } from '@saphir/module-contract';

/**
 * Class PrimeNG's `darkModeSelector` watches; see `app.config.ts`. Put on the
 * root element so both the application and PrimeNG's overlays — which render
 * outside it, at the end of `body` — see the same scheme.
 */
const DARK_CLASS = 'app-dark';

const STORAGE_KEY = 'saphir-web.color-scheme';

/**
 * The shell's light/dark scheme, and the implementation behind `SAPHIR_THEME`.
 *
 * The first visit follows the operating system. A deliberate choice is
 * remembered and from then on wins over it — someone who picked dark on a
 * machine that switches at sunset meant dark, not "until this evening".
 */
@Service()
export class ThemeService implements SaphirThemeContext {
  private readonly document = inject(DOCUMENT);

  private readonly scheme = signal<SaphirColorScheme>(this.initialScheme());

  readonly colorScheme = this.scheme.asReadonly();

  constructor() {
    // Applied straight away rather than from an effect: an effect would run
    // after the first render and show a light frame for a frame.
    this.apply(this.scheme());
  }

  toggle(): void {
    this.set(this.scheme() === 'dark' ? 'light' : 'dark');
  }

  set(scheme: SaphirColorScheme): void {
    this.scheme.set(scheme);
    this.apply(scheme);
    this.remember(scheme);
  }

  private apply(scheme: SaphirColorScheme): void {
    this.document.documentElement.classList.toggle(DARK_CLASS, scheme === 'dark');
  }

  private initialScheme(): SaphirColorScheme {
    const stored = read(STORAGE_KEY);

    if (stored === 'light' || stored === 'dark') {
      return stored;
    }

    return this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }

  private remember(scheme: SaphirColorScheme): void {
    write(STORAGE_KEY, scheme);
  }
}

// Storage is unavailable in private windows and behind some policies. Losing the
// preference is a small thing; failing to switch the theme is not.
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignored on purpose — see above.
  }
}
